/**
 * D5b: React hook for the "Recent work" mixed list.
 *
 * Tracks the last N touched workspace entities (clients / matters /
 * cases) across a session. Backed by localStorage so the list
 * survives a page refresh without a runtime round trip. Entries are
 * appended via `recordRecentWork()` which components call on
 * navigation events (handled by the Workspace tree + breadcrumbs in
 * D6+).
 *
 * The list deduplicates by `{kind, id}` — touching the same entity
 * twice moves it to the top without creating a duplicate row.
 *
 * Home widget + Workspace panel header both consume this hook (the
 * Home widget caps at 10, the Workspace panel header at 5 per the
 * design). That difference is the `limit` parameter.
 */

import { useCallback, useEffect, useState } from "react";

export type RecentWorkKind = "client" | "matter" | "case";

export interface RecentWorkEntry {
  kind: RecentWorkKind;
  id: string;
  label: string;
  /** Breadcrumb subtitle: `"Client › Matter"` for a case, `"Client"` for a matter, empty for a client. */
  subtitle?: string;
  /** Navigation URL. D6+ wires the short `/cases/:id` canonical for case rows. */
  href: string;
  /** Unix ms of the last touch. */
  touchedAt: number;
}

const STORAGE_KEY = "lexide:recent-work";
const MAX_ENTRIES = 20;
const STORAGE_EVENT = "lexide:recent-work:change";

function readStorage(): RecentWorkEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentWorkEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStorage(entries: RecentWorkEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    // Broadcast an in-tab event so every useRecentWork instance picks
    // up the change immediately. `storage` events only fire across
    // tabs, not within the same tab.
    window.dispatchEvent(new CustomEvent(STORAGE_EVENT));
  } catch {
    // Quota exceeded or localStorage disabled — fail silently; the
    // Recent widget is a nice-to-have, not load-bearing.
  }
}

/**
 * Record a touch on a workspace entity. Moves the entry to the
 * top of the list and dedupes by `{kind, id}`. Safe to call on
 * every render — callers should gate on an actual navigation
 * event rather than relying on effect-time deduping.
 */
export function recordRecentWork(entry: Omit<RecentWorkEntry, "touchedAt">): void {
  const existing = readStorage();
  const now = Date.now();
  const filtered = existing.filter((e) => !(e.kind === entry.kind && e.id === entry.id));
  const next = [{ ...entry, touchedAt: now }, ...filtered].slice(0, MAX_ENTRIES);
  writeStorage(next);
}

export interface UseRecentWorkOptions {
  /** Maximum entries to return. Default 10 (Home widget). */
  limit?: number;
  /** Optional kind filter. */
  kinds?: RecentWorkKind[];
}

export function useRecentWork(options: UseRecentWorkOptions = {}): RecentWorkEntry[] {
  const { limit = 10, kinds } = options;
  const [entries, setEntries] = useState<RecentWorkEntry[]>(() => readStorage());

  const refresh = useCallback(() => {
    setEntries(readStorage());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onChange = () => refresh();
    window.addEventListener(STORAGE_EVENT, onChange);
    // Cross-tab sync (localStorage fires `storage` for *other* tabs).
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(STORAGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [refresh]);

  const filtered = kinds ? entries.filter((e) => kinds.includes(e.kind)) : entries;
  return filtered.slice(0, limit);
}
